import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  DistributePaymentRequest,
  ReturnPaymentRequest,
  UndistributedPayment,
  UndistributedPaymentListItem,
} from './types';

export const undistributedPaymentsApi = createApi({
  reducerPath: 'undistributedPaymentsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/undistributed-payments',
  }),
  tagTypes: ['UndistributedPayments'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getUndistributedPayments: builder.query<
      UndistributedPaymentListItem[],
      void
    >({
      query: params => ({
        url: ``,
        params,
      }),
      providesTags: ['UndistributedPayments'],
    }),
    getUndistributedPaymentListI: builder.infiniteQuery<
      (UndistributedPaymentListItem & { id: string })[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['UndistributedPayments'],
    }),
    getUndistributedPaymentById: builder.query<UndistributedPayment, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: 'UndistributedPayments', id }],
    }),
    distributePayment: builder.mutation<void, DistributePaymentRequest>({
      query: ({ id, contractId, comment }) => ({
        url: `/${id}/distribute`,
        method: 'PUT',
        data: { contractId, comment },
      }),
      invalidatesTags: ['UndistributedPayments'],
    }),
    returnPayment: builder.mutation<void, ReturnPaymentRequest>({
      query: ({ id, reason, comment }) => ({
        url: `/${id}/return`,
        method: 'PUT',
        data: { reason, comment },
      }),
      invalidatesTags: ['UndistributedPayments'],
    }),
  }),
});

export const {
  useGetUndistributedPaymentsQuery,
  useGetUndistributedPaymentByIdQuery,
  useDistributePaymentMutation,
  useReturnPaymentMutation,
} = undistributedPaymentsApi;

export const useGetUndistributedPaymentListIInfiniteQuery =
  normalizeInfiniteQueryData(
    undistributedPaymentsApi.useGetUndistributedPaymentListIInfiniteQuery,
  );
